import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Mail, Phone, MapPin, Instagram, Facebook, Youtube, Send, Loader2 } from 'lucide-react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';

export default function Contact() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
    honeypot: ''
  });

  useEffect(() => {
    api.get('/public/settings').then(res => {
      setSettings(res.data);
      setLoading(false);
    }).catch(err => {
      console.error(err);
      setLoading(false);
    });
  }, []);

  // Helper per ottenere testi CMS con fallback
  const t = (key, fallback) => settings?.texts?.[key] || fallback;

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.honeypot) {
      toast.error(t('msg_error_generic', 'Richiesta non valida'));
      return;
    }

    if (!formData.name || !formData.email || !formData.message) {
      toast.error(t('msg_error_required', 'Compila tutti i campi obbligatori'));
      return;
    }

    setSubmitting(true);
    try {
      await api.post('/leads/', formData);
      setSubmitted(true);
      toast.success(t('msg_contact_success', 'Messaggio inviato! Ti risponderò al più presto.'));
    } catch (error) {
      console.error('Submit error:', error);
      const message = error.response?.data?.detail || t('msg_error_generic', 'Errore nell\'invio del messaggio');
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-32 flex items-center justify-center">
        <div className="animate-pulse text-muted-foreground">Caricamento...</div>
      </div>
    );
  }

  return (
    <div className="pt-32 pb-24" data-testid="contact-page">
      {/* Header */}
      <div className="max-w-[1800px] mx-auto px-6 lg:px-12 mb-16">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center"
        >
          <span className="text-sm tracking-[0.2em] uppercase text-muted-foreground">Contatti</span>
          <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl mt-4 mb-6">
            {t('page_contact_title', 'Parliamo')}
          </h1>
          <p className="font-accent text-xl text-muted-foreground max-w-2xl mx-auto">
            {t('page_contact_subtitle', 'Hai un progetto in mente? Contattami e discutiamo insieme dei dettagli.')}
          </p>
        </motion.div>
      </div>

      <div className="max-w-[1800px] mx-auto px-6 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
          {/* Contact Info */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <h2 className="font-heading text-3xl mb-8">{t('contact_info_title', 'Informazioni di Contatto')}</h2>
            
            <div className="space-y-6 mb-12">
              {settings?.contact_email && (
                <a
                  href={`mailto:${settings.contact_email}`}
                  className="flex items-center gap-4 p-4 bg-muted/50 hover:bg-muted transition-colors"
                  data-testid="contact-email"
                >
                  <Mail size={24} />
                  <div>
                    <div className="text-sm text-muted-foreground">{t('form_email', 'Email')}</div>
                    <div className="font-medium">{settings.contact_email}</div>
                  </div>
                </a>
              )}

              {settings?.contact_phone && (
                <a
                  href={`tel:${settings.contact_phone}`}
                  className="flex items-center gap-4 p-4 bg-muted/50 hover:bg-muted transition-colors"
                  data-testid="contact-phone"
                >
                  <Phone size={24} />
                  <div>
                    <div className="text-sm text-muted-foreground">{t('form_phone', 'Telefono')}</div>
                    <div className="font-medium">{settings.contact_phone}</div>
                  </div>
                </a>
              )}

              {settings?.contact_address && (
                <div className="flex items-center gap-4 p-4 bg-muted/50">
                  <MapPin size={24} />
                  <div>
                    <div className="text-sm text-muted-foreground">{t('contact_address_label', 'Indirizzo')}</div>
                    <div className="font-medium">{settings.contact_address}</div>
                  </div>
                </div>
              )}
            </div>

            {/* Social Links */}
            <h3 className="font-heading text-xl mb-4">{t('contact_social_title', 'Seguimi')}</h3>
            <div className="flex gap-4">
              {settings?.social_instagram && (
                <a
                  href={settings.social_instagram}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-12 h-12 flex items-center justify-center bg-muted hover:bg-foreground hover:text-background transition-colors"
                  aria-label="Instagram"
                  data-testid="social-instagram"
                >
                  <Instagram size={20} />
                </a>
              )}
              {settings?.social_facebook && (
                <a
                  href={settings.social_facebook}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-12 h-12 flex items-center justify-center bg-muted hover:bg-foreground hover:text-background transition-colors"
                  aria-label="Facebook"
                  data-testid="social-facebook"
                >
                  <Facebook size={20} />
                </a>
              )}
              {settings?.social_youtube && (
                <a
                  href={settings.social_youtube}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-12 h-12 flex items-center justify-center bg-muted hover:bg-foreground hover:text-background transition-colors"
                  aria-label="YouTube"
                  data-testid="social-youtube"
                >
                  <Youtube size={20} />
                </a>
              )}
            </div>
          </motion.div>

          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="bg-card border border-border p-8 lg:p-12"
          >
            {submitted ? (
              <div className="text-center py-12" data-testid="contact-submitted">
                <div className="w-16 h-16 bg-foreground text-background rounded-full flex items-center justify-center mx-auto mb-6">
                  <Send size={28} />
                </div>
                <h2 className="font-heading text-3xl mb-4">{t('contact_success_title', 'Messaggio Inviato!')}</h2>
                <p className="text-muted-foreground">
                  {t('msg_contact_success', 'Grazie per avermi contattato. Ti risponderò al più presto.')}
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} data-testid="contact-form">
                <h2 className="font-heading text-3xl mb-8">{t('contact_form_title', 'Inviami un Messaggio')}</h2>

                <div className="mb-6">
                  <Label className="mb-3 block">{t('form_name', 'Nome')} *</Label>
                  <Input
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder={t('form_name_placeholder', 'Il tuo nome')}
                    className="input-artistic"
                    data-testid="contact-name-input"
                  />
                </div>

                <div className="mb-6">
                  <Label className="mb-3 block">{t('form_email', 'Email')} *</Label>
                  <Input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder={t('form_email_placeholder', 'La tua email')}
                    className="input-artistic"
                    data-testid="contact-email-input"
                  />
                </div>

                <div className="mb-6">
                  <Label className="mb-3 block">{t('form_phone', 'Telefono')}</Label>
                  <Input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder={t('form_phone_placeholder', 'Il tuo numero di telefono')}
                    className="input-artistic"
                    data-testid="contact-phone-input"
                  />
                </div>

                <div className="mb-8">
                  <Label className="mb-3 block">{t('form_message', 'Messaggio')} *</Label>
                  <Textarea
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    rows={5}
                    placeholder={t('form_message_placeholder', 'Scrivi il tuo messaggio...')}
                    className="resize-none"
                    data-testid="contact-message-input"
                  />
                </div>

                {/* Honeypot */}
                <input
                  type="text"
                  name="honeypot"
                  value={formData.honeypot}
                  onChange={handleInputChange}
                  className="absolute opacity-0 pointer-events-none"
                  tabIndex={-1}
                  autoComplete="off"
                />

                <Button type="submit" disabled={submitting} className="w-full" data-testid="contact-submit-btn">
                  {submitting ? (
                    <>
                      <Loader2 size={16} className="mr-2 animate-spin" />
                      {t('btn_sending', 'Invio in corso...')}
                    </>
                  ) : (
                    <>
                      {t('btn_send', 'Invia Messaggio')}
                      <Send size={16} className="ml-2" />
                    </>
                  )}
                </Button>
              </form>
            )}
          </motion.div>
        </div>
      </div>
    </div>
  );
}
